"use client";

// Documentation content — English. Mirrors public/documentation-en.html.
// Plain-language edition, refactored to cover ownership levels and the
// on-page management tools.

import {
  Callout,
  DocBadge,
  DocTable,
  L,
  OL,
  P,
  type DocSection,
} from "./primitives";

export const sectionsEN: DocSection[] = [
  // ---- Part 1 · Start here ----
  {
    id: "what-is-homebook",
    num: "1.1",
    part: 1,
    title: "What is HomeBook?",
    body: (
      <>
        <P>
          HomeBook is a shared family calendar for our houses. Every house has its
          own page with photos, prices and a calendar. You pick your days, and the
          people in charge say yes or no. That is the whole idea.
        </P>
        <L>
          <li>
            It works on any phone, tablet or computer — nothing to install.
          </li>
          <li>
            Every person signs in with their own account, so the calendar always
            shows who is staying.
          </li>
        </L>
      </>
    ),
  },
  {
    id: "test-accounts",
    num: "1.2",
    part: 1,
    title: "Test accounts",
    body: (
      <>
        <P>
          The site comes with ready-made accounts so you can try each role. Sign in
          with the <strong>username</strong> (not the e-mail):
        </P>
        <DocTable
          head={["Username", "Password", "What this account is for"]}
          rows={[
            [
              <code key="a">admin</code>,
              <code key="ap">{"admin" + "123"}</code>,
              "The boss. Sees and does everything: users, houses, money, settings.",
            ],
            [
              <code key="m">mod</code>,
              <code key="mp">{"mod" + "123"}</code>,
              "A helper. Can be assigned to houses to approve bookings and handle bills.",
            ],
            [
              <code key="f">family</code>,
              <code key="fp">{"owner" + "123"}</code>,
              "A house owner. Owns the demo houses and decides who stays.",
            ],
            [
              <code key="g">guest</code>,
              <code key="gp">{"guest" + "123"}</code>,
              "A regular family guest with existing reservations.",
            ],
            [
              <code key="c">cousin</code>,
              <code key="cp">{"cousin" + "123"}</code>,
              "A regular user — try the family discount with this one.",
            ],
          ]}
        />
        <Callout tone="warn" label="Before real use">
          <P>
            These passwords are written on a public page. Before the family really
            uses the site, change them — the admin can set a new password for any
            account in seconds.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "sign-in",
    num: "1.3",
    part: 1,
    title: "How to sign in",
    body: (
      <>
        <OL>
          <li>
            Open the site. At the top right, press <strong>Sign in</strong>.
          </li>
          <li>
            Type the username and the password. There is nothing else to fill.
          </li>
          <li>Press the button. You are inside.</li>
        </OL>
        <P>
          Your name appears at the top. Press it to change the language, the theme
          (day or night colors), or to leave (<strong>Sign out</strong>).
        </P>
      </>
    ),
  },

  // ---- Part 2 · The main flow ----
  {
    id: "book-a-stay",
    num: "2.1",
    part: 2,
    title: "Book a house (the main thing)",
    body: (
      <>
        <OL>
          <li>
            Press <strong>Browse</strong> at the top and open a house you like.
          </li>
          <li>
            Choose your days on the calendar: first night, last night. Green means
            free.
          </li>
          <li>Say how many people are coming.</li>
          <li>
            Answer one simple question: do you want the{" "}
            <strong>whole house</strong> (Private), or are you okay to{" "}
            <strong>share</strong> it (Shared)? Then press the request button.
          </li>
          <li>
            Wait for a yes. Your request first shows as{" "}
            <strong>pending</strong>; when it is approved, your name appears on
            those days and you get a message.
          </li>
        </OL>
        <Callout tone="note" label="Two ways to book">
          <P>
            You can also press a green day on the big calendar of your{" "}
            <strong>Dashboard</strong> — the same booking window opens there.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "read-the-calendar",
    num: "2.2",
    part: 2,
    title: "Reading the calendar",
    body: (
      <>
        <P>The calendar uses five colors. That is all you need to remember:</P>
        <DocTable
          head={["Color", "Meaning"]}
          rows={[
            [
              <span key="g" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-emerald-500" />
                Green
              </span>,
              "Free. You can ask for this day.",
            ],
            [
              <span key="r" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-rose-500" />
                Red
              </span>,
              "The whole house is taken (private booking).",
            ],
            [
              <span key="b" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-sky-400" />
                Blue
              </span>,
              "Shared stay — several people in the house.",
            ],
            [
              <span key="o" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm border-2 border-dashed border-amber-500" />
                Orange
              </span>,
              "Asked for, not approved yet.",
            ],
            [
              <span key="y" className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-zinc-300" />
                Grey
              </span>,
              "The house is closed (owner's choice).",
            ],
          ]}
        />
        <P>
          Press any day to see who is there. Owners and house staff will also find{" "}
          <strong>Approve</strong> and <strong>Decline</strong> buttons here for
          waiting requests.
        </P>
      </>
    ),
  },
  {
    id: "my-trips",
    num: "2.3",
    part: 2,
    title: "Your trips & cancelling",
    body: (
      <>
        <P>
          Everything you asked for lives under <strong>My Trips</strong> at the
          top: what is coming up, what is in the past, and the status of each
          request.
        </P>
        <L>
          <li>
            A request that is still <strong>pending</strong> can be cancelled by
            you at any time.
          </li>
          <li>
            An <strong>approved</strong> stay is cancelled by the owner or an
            admin — just ask them.
          </li>
        </L>
      </>
    ),
  },

  // ---- Part 3 · Owners, helpers & words ----
  {
    id: "who-does-what",
    num: "3.1",
    part: 3,
    title: "Who does what",
    body: (
      <>
        <P>
          Every account has one badge. The badge decides what the person sees and
          can do:
        </P>
        <DocTable
          head={["Badge", "Can do"]}
          rows={[
            [
              <DocBadge key="v" tone="neutral">
                Visitor
              </DocBadge>,
              "Only look at the houses.",
            ],
            [
              <DocBadge key="u" tone="neutral">
                User
              </DocBadge>,
              "Book houses, see own trips, pay bills assigned to them.",
            ],
            [
              <DocBadge key="o" tone="violet">
                OWNER
              </DocBadge>,
              "Owns one or more houses: full control over them, approves requests, sees their bills.",
            ],
            [
              <DocBadge key="m" tone="sky">
                MODERATOR
              </DocBadge>,
              "A helper. Admins can assign moderators to specific houses — then they approve bookings and handle bills for those houses only.",
            ],
            [
              <DocBadge key="a" tone="dark">
                ADMIN
              </DocBadge>,
              "Runs the site: users, roles, houses, settings, everything.",
            ],
          ]}
        />
        <P>
          An admin or an owner can create an account in under a minute — a
          username alone is enough, everything else is optional, and the site can
          even invent the password.
        </P>
      </>
    ),
  },
  {
    id: "manage-a-house",
    num: "3.2",
    part: 3,
    title: "Managing a house",
    body: (
      <>
        <P>
          Open a house page as its owner or as an admin and a{" "}
          <strong>manage bar</strong> appears at the top. Everything is editable
          right there, on the page:
        </P>
        <DocTable
          head={["Button", "What it does"]}
          rows={[
            ["Edit", "Change the title, description, price, family discount and family group."],
            ["Availability", "Choose which days the house can be booked."],
            ["Photos", "Add photos — or click any picture on the page to replace it directly."],
            ["Season prices", "Set a higher price for high-season dates."],
            ["Hide / Publish", "A hidden house disappears from the public list until you publish it again."],
            ["Delete", "Removes the house and its bookings (with a warning first)."],
          ]}
        />
        <P>
          Waiting requests also come to you: they appear on your{" "}
          <strong>Dashboard</strong> calendar and on the house page — open the day
          and press <strong>Approve</strong> or <strong>Decline</strong>.
        </P>
      </>
    ),
  },
  {
    id: "house-staff",
    num: "3.3",
    part: 3,
    title: "House staff & families",
    body: (
      <>
        <P>
          A house can have its own team. On the house page, press{" "}
          <strong>House staff</strong> and add any moderator: from that moment
          they can approve bookings and handle the bills of{" "}
          <strong>that house only</strong> — they see nothing else.
        </P>
        <P>
          Houses also belong to <strong>family groups</strong>. Members of a
          house's family automatically pay the family discount there — you choose
          the percentage when editing the house.
        </P>
        <Callout tone="note" label="Good to know">
          <P>
            The Permissions screen (admin only) lets you fine-tune what each role
            can do. The defaults are sensible — you only need it later.
          </P>
        </Callout>
      </>
    ),
  },
  {
    id: "bills-and-money",
    num: "3.4",
    part: 3,
    title: "Bills and money",
    body: (
      <>
        <P>
          Stays and house works create <strong>invoices</strong>: each has a
          number, an amount, and one person who has to pay it.
        </P>
        <L>
          <li>
            The <strong>Expenses</strong> page has a house switcher at the top —
            pick a house to see only its bills.
          </li>
          <li>
            Every waiting bill shows an{" "}
            <strong>assignment chip</strong>. Press it and pick the family member
            who has to pay — you can change it at any time.
          </li>
          <li>
            When the money arrives, press <strong>Mark paid</strong> and choose
            who paid.
          </li>
          <li>
            The page also shows <strong>spending by month</strong> and{" "}
            <strong>where the money goes</strong> per category.
          </li>
        </L>
      </>
    ),
  },
  {
    id: "small-dictionary",
    num: "3.5",
    part: 3,
    title: "Small dictionary",
    body: (
      <>
        <P>
          The words you will meet on the site — translated from computer-speak
          into normal:
        </P>
        <DocTable
          head={["Word on the site", "What it means"]}
          rows={[
            ["Sign in / Sign out", "Enter the site / leave it."],
            ["Browse", "Look at the houses."],
            ["Dashboard", "Your personal summary page."],
            ["My Trips", "Your requests and stays."],
            ["Pending", "Sent, waiting for a yes."],
            ["Approved / Rejected", "The owner or staff said yes / no."],
            ["Private", "You have the whole house."],
            ["Shared", "You share the house with others."],
            ["Availability", "The days a house can be booked."],
            ["Hide / Publish", "Take a house off / put it back on the public list."],
            ["House staff", "Moderators assigned to one house."],
            ["Invoice", "A bill: what, how much, who pays."],
            ["Assignment", "The person who has to pay a bill."],
            ["Write off", "Cancel a bill politely (nobody pays it)."],
          ]}
        />
      </>
    ),
  },
  {
    id: "small-help",
    num: "3.6",
    part: 3,
    title: "Small help",
    body: (
      <>
        <L>
          <li>
            <strong>Forgot your password?</strong> Ask an admin (the{" "}
            <code>admin</code> account, or whoever runs the site) — they can set
            you a new one in seconds.
          </li>
          <li>
            <strong>A day refuses to be picked?</strong> It is taken, or the house
            is closed. Try other days.
          </li>
          <li>
            <strong>Missing a house in your Expenses list?</strong> Use the house
            switcher at the top and pick <em>All houses</em>.
          </li>
          <li>
            <strong>Wrong language?</strong> Press the flag at the top and choose
            yours.
          </li>
          <li>
            <strong>Something looks broken?</strong> Leave the page and come back
            — it fixes most small glitches. If not, tell the person who runs the
            site.
          </li>
        </L>
      </>
    ),
  },
];
